/**
 * Sub-Dealer Transaction Types — admin master list.
 *
 * Lets admin manage the dropdown options that appear on the Sub-Dealer
 * "Add Ledger Entry" form. Each type has a code (uppercase), display
 * label, direction (debit/credit), and sort order. System-seeded types
 * cannot be deleted (the API guards that) but can be deactivated.
 *
 * Duplicate detection: groups by uppercase code on the client and shows
 * a banner with a "review duplicates" anchor when two rows share a code.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import Drawer, { FormField } from "@/components/Drawer";
import { Plus, Trash, ArrowLeft, Warning } from "@phosphor-icons/react";

const BLANK = { name: "", label: "", direction: "debit", sort_order: 0 };

export default function SubDealerTxnTypesPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/sub-dealers/transaction-types");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dupGroups = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const k = (r.name || "").toUpperCase();
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    });
    return [...map.entries()].filter(([, arr]) => arr.length > 1);
  }, [rows]);

  const toggleActive = async (row) => {
    try {
      await api.put(`/sub-dealers/transaction-types/${row.id}`, {
        name: row.name, label: row.label, direction: row.direction,
        sort_order: row.sort_order, active: !row.active,
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (row) => {
    if (row.is_system) { toast.error("System types can't be deleted — deactivate instead."); return; }
    if (!window.confirm(`Delete transaction type "${row.label}"?`)) return;
    try {
      await api.delete(`/sub-dealers/transaction-types/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Sub-Dealers / Masters"
        title="Transaction Types"
        subtitle="Manage the dropdown options for ledger entries — code, label, direction, and ordering."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => nav("/sub-dealers")}
              data-testid="back-to-sub-dealers"
              className="px-3 py-2.5 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:bg-canvas-panel flex items-center gap-1.5"
            >
              <ArrowLeft size={12} weight="bold" /> Back
            </button>
            <button
              onClick={() => { setEditing(null); setShowForm(true); }}
              data-testid="new-txn-type"
              className="bg-ink text-white px-3 py-2.5 text-xs font-mono uppercase tracking-wider hover:bg-brand flex items-center gap-1.5"
            >
              <Plus size={12} weight="bold" /> New Type
            </button>
          </div>
        }
      />

      {dupGroups.length > 0 ? (
        <div className="border border-amber-300 bg-amber-50/60 px-3 py-2 flex items-start gap-2 text-sm">
          <Warning size={16} weight="bold" className="text-amber-700 mt-0.5 shrink-0" />
          <div>
            <span className="font-semibold text-amber-800">{dupGroups.length} duplicate code(s) found.</span>{" "}
            Each code below appears more than once. Keep one row active and deactivate or delete the rest:
            <ul className="font-mono text-[11px] mt-1.5 text-amber-900">
              {dupGroups.map(([code, arr]) => (
                <li key={code}>· {code} — {arr.length} entries</li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      <div className="border border-canvas-line">
        <table className="w-full text-sm">
          <thead className="bg-canvas-panel border-b border-canvas-line">
            <tr>
              {["Code", "Label", "Direction", "Sort", "System", "Active", ""].map((h) => (
                <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const dup = dupGroups.some(([code]) => code === (r.name || "").toUpperCase());
              return (
                <tr key={r.id} className={`border-b border-canvas-line/50 ${dup ? "bg-amber-50/40" : ""}`} data-testid={`txn-type-row-${r.name}`}>
                  <td className="px-3 py-2 font-mono font-semibold">{r.name}</td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => { setEditing(r); setShowForm(true); }}
                      className="text-left hover:underline"
                    >
                      {r.label}
                    </button>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] uppercase">{r.direction}</td>
                  <td className="px-3 py-2 font-mono">{r.sort_order ?? 0}</td>
                  <td className="px-3 py-2 text-xs text-ink-muted">{r.is_system ? "yes" : ""}</td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => toggleActive(r)}
                      data-testid={`txn-type-toggle-${r.name}`}
                      className={`px-2 py-0.5 font-mono text-[10px] uppercase ${r.active ? "bg-emerald-50 text-emerald-700" : "bg-canvas-line/50 text-ink-subtle"}`}
                    >
                      {r.active ? "active" : "inactive"}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!r.is_system ? (
                      <button onClick={() => remove(r)} className="text-rose-700 hover:bg-rose-50 p-1" data-testid={`txn-type-delete-${r.name}`}>
                        <Trash size={12} />
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <TypeFormDrawer
        open={showForm}
        editing={editing}
        onClose={() => setShowForm(false)}
        onSaved={() => { setShowForm(false); load(); }}
      />
    </div>
  );
}


function TypeFormDrawer({ open, editing, onClose, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(editing ? { ...editing } : BLANK);
  }, [open, editing]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        name: form.name.trim().toUpperCase(),
        label: form.label.trim(),
        direction: form.direction,
        sort_order: Number(form.sort_order) || 0,
      };
      if (editing) await api.put(`/sub-dealers/transaction-types/${editing.id}`, body);
      else await api.post("/sub-dealers/transaction-types", body);
      toast.success(editing ? "Updated" : "Created");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Drawer open={open} onClose={onClose} eyebrow="Masters" title={editing ? "Edit Type" : "New Transaction Type"}>
      <form onSubmit={save} className="space-y-4">
        <FormField label="Code (UPPER_SNAKE)" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="type-name" mono required />
        <FormField label="Label (shown in dropdown)" value={form.label} onChange={(v) => setForm({ ...form, label: v })} testId="type-label" required />
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">Direction</label>
          <select
            value={form.direction}
            onChange={(e) => setForm({ ...form, direction: e.target.value })}
            data-testid="type-direction"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          >
            <option value="debit">debit (we owe / they pay)</option>
            <option value="credit">credit (they owe / we paid)</option>
          </select>
        </div>
        <FormField label="Sort order (lower = top)" type="number" value={form.sort_order} onChange={(v) => setForm({ ...form, sort_order: v })} testId="type-sort" mono />
        <div className="pt-4 flex items-center gap-2 justify-end border-t border-canvas-line -mx-6 px-6">
          <button type="button" onClick={onClose} className="px-4 py-2.5 border border-canvas-line text-sm hover:border-ink">Cancel</button>
          <button type="submit" disabled={saving} data-testid="type-save" className="px-4 py-2.5 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-60">
            {saving ? "Saving…" : (editing ? "Save" : "Create")}
          </button>
        </div>
      </form>
    </Drawer>
  );
}
